import React, { useState, useCallback, useEffect, type MouseEvent } from 'react';
import noop from 'lodash/noop';
import Button from '@atlaskit/button';
import EditorEditIcon from '@atlaskit/icon/core/migration/edit--editor-edit';
// @ts-expect-error - TS2305 - Module '"@atlaskit/side-navigation"' has no exported member 'CSSFn'.
import { type CSSFn, LinkItem } from '@atlaskit/side-navigation';
import { CardAction } from '@atlaskit/smart-card';
import { HoverCard } from '@atlaskit/smart-card/hover-card';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { fg } from '@atlassian/jira-feature-gating';
import { isVisualRefreshEnabled } from '@atlassian/jira-visual-refresh-rollout/src/feature-switch/index.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import { useIntl } from '@atlassian/jira-intl';
import type { StringValue } from '@atlassian/jira-issue-field-summary-common/src/services/types.tsx';
import messages from './messages.tsx';

export type Props = {
	isEditable: boolean;
	isConfigLoading?: boolean | undefined;
	isInlineEditEnabled?: boolean;
	url?: string;
	value: StringValue;
	setIsEditing?: (arg1: boolean) => void;
	onClick?: (event: MouseEvent) => void;
	showHoverPreviewEnabled?: boolean;
};

const HOVER_CARD_FADE_IN_TIME_MS = 500;

// NOTE: isEditable is users permission level for editing summary
// where as isInlineEditEnabled refers to whether or not
// summary field component inline editing is enabled. There are cases
// where we may not want field to inline edit. (i.e. Linked Issue Card summary field)
export const InlineSummaryRead = ({
	value,
	setIsEditing,
	url,
	isEditable,
	isConfigLoading = undefined,
	isInlineEditEnabled = true,
	onClick = noop,
	showHoverPreviewEnabled = false,
}: Props) => {
	const displayValue = value === null ? '' : value.toString();
	const [isHovered, setHovered] = useState(false);
	const [canOpenHoverCard, setCanOpenHoverCard] = useState(true);
	const [editButtonHovered, setEditButtonHovered] = useState(false);
	const { formatMessage } = useIntl();

	const handleMouseEnter = useCallback(() => {
		setHovered(true);
	}, []);

	const handleMouseLeave = useCallback(() => {
		setHovered(false);
	}, []);

	const getTooltipMessage = () => {
		if (isConfigLoading === undefined || isConfigLoading) {
			return '';
		}

		return !isConfigLoading && isEditable
			? formatMessage(messages.editSummary)
			: formatMessage(
					expVal('issue-terminology-refresh-m2-replace', 'isEnabled', false)
						? messages.noPermissionIssueTermRefresh
						: messages.noPermission,
				);
	};

	const handleClick = useCallback(
		// @ts-expect-error - TS7006 - Parameter 'e' implicitly has an 'any' type.
		(e) => {
			// We are preventing the default behavior of the element on click event
			// So it can invoke the onClick function without changing the original behavior
			e.preventDefault();
			onClick(e);
		},
		[onClick],
	);

	const handleEditButtonHover = useCallback(() => {
		if (showHoverPreviewEnabled) {
			setEditButtonHovered(true);
		}
	}, [showHoverPreviewEnabled]);

	const handleEditButtonLeave = useCallback(() => {
		if (showHoverPreviewEnabled) {
			setEditButtonHovered(false);
		}
	}, [showHoverPreviewEnabled]);

	useEffect(() => {
		if (showHoverPreviewEnabled) {
			if (editButtonHovered) {
				// hiding the hover card if a user hovers on edit button
				setCanOpenHoverCard(false);
			} else if (!canOpenHoverCard) {
				const timeout = setTimeout(() => {
					// showing the hover card again after a delay if a user moves away from the edit button
					setCanOpenHoverCard(true);
				}, HOVER_CARD_FADE_IN_TIME_MS);
				return () => clearTimeout(timeout);
			}
		}
		// to satisfy consistent return rule
		// eslint-disable-next-line @typescript-eslint/no-empty-function
		return () => {};
	}, [canOpenHoverCard, showHoverPreviewEnabled, editButtonHovered]);

	const editButtonContent = (
		<Tooltip content={getTooltipMessage()}>
			<Button
				testId="issue-field-summary.ui.inline-read.edit-button"
				onClick={(e) => {
					e.stopPropagation();
					// We are preventing the default behavior of the element on click event
					// So preventing the link item's navigation-on-click behaviour from mucking around with the inline edit behaviour.
					e.preventDefault();
					handleMouseLeave();
					isEditable && setIsEditing && setIsEditing(true);
				}}
				iconAfter={
					<EditorEditIcon
						label={formatMessage(messages.editSummary)}
						color={!isEditable ? token('color.icon.subtle', colors.N60) : 'currentColor'}
					/>
				}
				appearance={
					isConfigLoading === undefined || isConfigLoading || !isEditable ? 'link' : 'subtle'
				}
				onBlur={handleMouseLeave}
				interactionName="auto-generated.issue-field-summary.ui.inline-read.edit-button.button"
			/>
		</Tooltip>
	);

	const editButton = showHoverPreviewEnabled ? (
		// eslint-disable-next-line jsx-a11y/no-static-element-interactions
		<div onMouseEnter={handleEditButtonHover} onMouseLeave={handleEditButtonLeave}>
			{editButtonContent}
		</div>
	) : (
		editButtonContent
	);

	const linkItemContent = (
		<LinkItem
			href={url}
			testId="issue-field-summary.ui.inline-read.link-item"
			// eslint-disable-next-line @atlaskit/design-system/no-deprecated-apis
			cssFn={linkItemStyles}
			onClick={handleClick}
			// @ts-expect-error - TS2322 - Type '{ children: string; href: string | undefined; testId: string; cssFn: CSSFn; onClick: (e: any) => void; onMouseOver: () => void; onMouseLeave: () => void; onFocus: () => void; iconAfter: false | Element; tabIndex: string; }' is not assignable to type 'IntrinsicAttributes & LinkItemProps & RefAttributes<HTMLElement>'.
			onMouseOver={handleMouseEnter}
			onMouseLeave={handleMouseLeave}
			onFocus={handleMouseEnter}
			iconAfter={isInlineEditEnabled && isHovered && editButton}
			tabIndex="0"
		>
			{displayValue}
		</LinkItem>
	);

	if (showHoverPreviewEnabled) {
		const issueLink = url?.startsWith('/')
			? // eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
				`${window.location.protocol}//${window.location.hostname}${url}`
			: url;

		return (
			<HoverCard
				url={issueLink || ''}
				canOpen={canOpenHoverCard}
				closeOnChildClick
				actionOptions={{
					hide: false,
					exclude: [CardAction.ChangeStatusAction],
				}}
			>
				{linkItemContent}
			</HoverCard>
		);
	}

	return (
		<Tooltip content={displayValue} position="bottom" hideTooltipOnMouseDown>
			{linkItemContent}
		</Tooltip>
	);
};

// @ts-expect-error - TS7006 - Parameter 'currentStyles' implicitly has an 'any' type.
export const linkItemStyles: CSSFn = (currentStyles) => ({
	...currentStyles,
	display: 'flex',
	padding:
		isVisualRefreshEnabled() && fg('visual-refresh_drop_5')
			? '0 0 1px 0'
			: `0 0 1px ${token('space.075')}`,
	backgroundColor: 'transparent',
	borderTop: '2px solid transparent',
	borderBottom: '2px solid transparent',
	borderLeft: '2px solid transparent',
	borderRight: 'none',
	color: token('color.text'),

	...(isVisualRefreshEnabled() && fg('visual-refresh_drop_4')
		? {
				' > [data-testid="issue-field-summary.ui.inline-read.link-item--primitive--container"]': {
					display: 'flex',
					gap: '4px',
					width: '100%',
					justifyContent: 'flex-start',
					// Overriding the styles of MenuItemPrimitive -> title container which is using Stack and grow="fill"
					// doing this in-order for the title to not take width of available container
					' > div:first-child': {
						flexGrow: '0',
						width: 'fit-content',
					},
				},
			}
		: {}),

	'&:hover': {
		color: token('color.text'),
		backgroundColor: 'transparent',
		cursor: 'pointer',
		textDecoration: 'underline',
	},
	'&:focus, &:active': {
		color: token('color.text'),
		outline: 'none',
		boxShadow: 'none',
		backgroundColor: 'transparent',
	},
	'& [data-item-elem-after]': {
		opacity: '1',
	},
	'& span': {
		flexGrow: 0,
	},
	'&[aria-disabled="true"]': {
		cursor: 'not-allowed',
		backgroundColor: 'transparent!important',
		textDecoration: 'none',
	},
	'& [data-item-title]:hover': {
		textDecoration: 'underline',
	},
	'& button': {
		height: '24px',
		width: '24px',
	},
});
